import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserAcademyName, getUserDisplayName } from '../api/auth';
import { AcademyMember, membersAPI, normalizeMembersPayload } from '../api/members';
import { useAuth } from '../context/AuthContext';

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

type CheckedDayItem = {
  day: string;
  condition: string;
};

type StudentAttendanceRow = {
  key: string;
  tauserId: string;
  name: string;
  schoolName: string;
  memberClass: string;
  memo: string;
  phone: string;
  parentPhone: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  eventdayMs: number | null;
  checkedDays: CheckedDayItem[];
};

type RemedialAttendanceRow = StudentAttendanceRow & {
  eventdayYmd: string;
  eventdayTime: string;
};

const dayOptions: Array<{ key: DayKey; label: string }> = [
  { key: 'monday', label: '월' },
  { key: 'tuesday', label: '화' },
  { key: 'wednesday', label: '수' },
  { key: 'thursday', label: '목' },
  { key: 'friday', label: '금' },
  { key: 'saturday', label: '토' },
  { key: 'sunday', label: '일' },
];

const dayOrder: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const getTodayDayKey = (): DayKey => {
  const day = new Date().getDay();
  const map: Record<number, DayKey> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  return map[day] || 'monday';
};

const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^0-9+]/g, '');
};

const getTauserId = (member: AcademyMember): string => {
  const rawId = member.tauser_id ?? member.tauserId ?? member.id;

  if (typeof rawId === 'number') {
    return String(rawId);
  }

  return normalizeText(rawId);
};

const getTodayDateString = (): string => {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateToYmd = (date: Date): string => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthDay = (date: Date): string => {
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const getMondayOfWeek = (date: Date): Date => {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const MAX_CHECKED_DAYS = 7;

const normalizeCheckedDays = (value: unknown): CheckedDayItem[] => {
  const parseItems = (raw: unknown): CheckedDayItem[] => {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const day = normalizeText((item as any).day);
        const condition = normalizeText((item as any).condition);
        if (!day || !condition) {
          return null;
        }

        return { day, condition };
      })
      .filter((item): item is CheckedDayItem => item !== null);
  };

  if (Array.isArray(value)) {
    return parseItems(value);
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) {
      return [];
    }

    try {
      return parseItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  return [];
};

const conditionToDisplayStatus = (condition: string): '출석' | '결석' | '대기' => {
  const normalized = normalizeText(condition);

  if (normalized === '출석') {
    return '출석';
  }

  if (normalized === '결석') {
    return '결석';
  }

  return '대기';
};

const quickStatusOptions: Array<'대기' | '출석' | '결석'> = ['대기', '출석', '결석'];

const getDisplayStatusByDate = (checkedDays: CheckedDayItem[], ymd: string): '출석' | '결석' | '대기' => {
  const matched = checkedDays.find((item) => item.day === ymd);
  return conditionToDisplayStatus(matched?.condition || '');
};

const getMemberKey = (member: AcademyMember, index: number): string => {
  const keyParts = [member.id, member.email, member.phone, member.uniqueKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('-') : `member-${index}`;
};

const getScheduleText = (value: unknown): string => {
  return normalizeText(value);
};

const getDayKeyFromDate = (date: Date): DayKey => {
  const day = date.getDay();
  const map: Record<number, DayKey> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  return map[day] || 'monday';
};

const parseEventdayTimestamp = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  const millis = parsed < 1_000_000_000_000 ? parsed * 1000 : parsed;
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return millis;
};

const formatDateToTime = (date: Date): string => {
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${hour}:${minute}`;
};

const parseTimeToSortableNumber = (value: string): number => {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour * 60 + minute;
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const AdminAttendanceCheckPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<AcademyMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersMessage, setMembersMessage] = useState('');
  const [selectedDay, setSelectedDay] = useState<DayKey>(getTodayDayKey());
  const [selectedStudent, setSelectedStudent] = useState<StudentAttendanceRow | null>(null);
  const [editMemo, setEditMemo] = useState('');
  const [isSavingMemo, setIsSavingMemo] = useState(false);
  const [detailMessage, setDetailMessage] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(getTodayDateString());
  const [attendanceTime, setAttendanceTime] = useState('00:00');
  const [isSavingEventday, setIsSavingEventday] = useState(false);
  const [quickStatusCooldownMap, setQuickStatusCooldownMap] = useState<Record<string, boolean>>({});
  const [regularSearchQuery1, setRegularSearchQuery1] = useState('');
  const [regularSearchQuery2, setRegularSearchQuery2] = useState('');

  const [todayBase] = useState(() => new Date());

  const academyName = getUserAcademyName(user);
  const displayName = getUserDisplayName(user);

  const studentRows = useMemo<StudentAttendanceRow[]>(() => {
    return members
      .map((member, index) => {
        const role = normalizeText(member.role).toLowerCase();
        if (role !== 'student') {
          return null;
        }

        return {
          key: getMemberKey(member, index),
          tauserId: getTauserId(member),
          name:
            normalizeText(member.name) ||
            normalizeText(member.username) ||
            normalizeText(member.email) ||
            `학생 ${index + 1}`,
          schoolName: normalizeText(member.schoolName) || '-',
          memberClass: normalizeText(member.class) || '-',
          memo: normalizeText(member.memo) || '-',
          phone: normalizeText(member.phone) || '-',
          parentPhone: normalizeText(member.parentPhone || member.parent_phone) || '-',
          monday: getScheduleText(member.monday),
          tuesday: getScheduleText(member.tuesday),
          wednesday: getScheduleText(member.wednesday),
          thursday: getScheduleText(member.thursday),
          friday: getScheduleText(member.friday),
          saturday: getScheduleText(member.saturday),
          sunday: getScheduleText(member.sunday),
          eventdayMs: parseEventdayTimestamp(member.eventday),
          checkedDays: normalizeCheckedDays(member.checked_days),
        };
      })
      .filter((row): row is StudentAttendanceRow => row !== null);
  }, [members]);

  const filteredRows = useMemo(() => {
    return studentRows
      .filter((row) => normalizeText(row[selectedDay]).length > 0)
      .sort((a, b) => {
        const leftTime = parseTimeToSortableNumber(a[selectedDay]);
        const rightTime = parseTimeToSortableNumber(b[selectedDay]);

        if (leftTime !== rightTime) {
          return leftTime - rightTime;
        }

        return a.name.localeCompare(b.name, 'ko');
      });
  }, [selectedDay, studentRows]);

  const selectedWeekDates = useMemo(() => {
    const monday = getMondayOfWeek(todayBase);
    const entries = dayOrder.map((key, index) => {
      const nextDate = new Date(monday);
      nextDate.setDate(monday.getDate() + index);
      return {
        key,
        ymd: formatDateToYmd(nextDate),
        monthDay: formatMonthDay(nextDate),
      };
    });

    return entries.reduce<Record<DayKey, { ymd: string; monthDay: string }>>((acc, item) => {
      acc[item.key] = { ymd: item.ymd, monthDay: item.monthDay };
      return acc;
    }, {
      monday: { ymd: '', monthDay: '' },
      tuesday: { ymd: '', monthDay: '' },
      wednesday: { ymd: '', monthDay: '' },
      thursday: { ymd: '', monthDay: '' },
      friday: { ymd: '', monthDay: '' },
      saturday: { ymd: '', monthDay: '' },
      sunday: { ymd: '', monthDay: '' },
    });
  }, [todayBase]);

  const selectedDayYmd = selectedWeekDates[selectedDay].ymd;
  const mondayYmd = selectedWeekDates.monday.ymd;
  const sundayYmd = selectedWeekDates.sunday.ymd;

  const filteredRegularRows = useMemo(() => {
    const q1 = regularSearchQuery1.trim().toLowerCase();
    const q2 = regularSearchQuery2.trim().toLowerCase();

    const matchesQuery = (row: StudentAttendanceRow, query: string): boolean => {
      if (!query) {
        return true;
      }

      const statusText = getDisplayStatusByDate(row.checkedDays, selectedDayYmd);

      return (
        statusText.toLowerCase().includes(query) ||
        row.name.toLowerCase().includes(query) ||
        row.schoolName.toLowerCase().includes(query) ||
        row.memberClass.toLowerCase().includes(query) ||
        row.memo.toLowerCase().includes(query)
      );
    };

    return filteredRows.filter((row) => matchesQuery(row, q1) && matchesQuery(row, q2));
  }, [filteredRows, regularSearchQuery1, regularSearchQuery2, selectedDayYmd]);

  const remedialRows = useMemo<RemedialAttendanceRow[]>(() => {
    return studentRows
      .map((row) => {
        if (!row.eventdayMs) {
          return null;
        }

        const eventdayDate = new Date(row.eventdayMs);
        const eventdayYmd = formatDateToYmd(eventdayDate);
        const eventdayDayKey = getDayKeyFromDate(eventdayDate);

        if (eventdayYmd < mondayYmd || eventdayYmd > sundayYmd) {
          return null;
        }

        if (eventdayDayKey !== selectedDay) {
          return null;
        }

        return {
          ...row,
          eventdayYmd,
          eventdayTime: formatDateToTime(eventdayDate),
        };
      })
      .filter((row): row is RemedialAttendanceRow => row !== null)
      .sort((a, b) => {
        const leftTime = parseTimeToSortableNumber(a.eventdayTime);
        const rightTime = parseTimeToSortableNumber(b.eventdayTime);

        if (leftTime !== rightTime) {
          return leftTime - rightTime;
        }

        return a.name.localeCompare(b.name, 'ko');
      });
  }, [mondayYmd, selectedDay, studentRows, sundayYmd]);

  const selectedPhone = selectedStudent ? sanitizePhone(selectedStudent.phone) : '';
  const selectedParentPhone = selectedStudent ? sanitizePhone(selectedStudent.parentPhone) : '';
  const canCallStudent = selectedPhone.length > 0;
  const canCallParent = selectedParentPhone.length > 0;
  const attendanceNoticeMessage = selectedStudent
    ? `[${academyName || '학원'}]\n[${selectedStudent.name}]\n학생이 출석하였습니다.`
    : '';
  const attendanceNoticeSmsHref = canCallParent
    ? `sms:${selectedParentPhone}?body=${encodeURIComponent(attendanceNoticeMessage)}`
    : undefined;

  const loadMembers = useCallback(async () => {
    if (!academyName) {
      setMembers([]);
      setMembersMessage('관리자 계정에 학원명이 없어 회원 목록을 조회할 수 없습니다.');
      setIsLoadingMembers(false);
      return;
    }

    setIsLoadingMembers(true);

    try {
      setMembersMessage('');
      const response = await membersAPI.getAcademyMembers(academyName);
      setMembers(normalizeMembersPayload(response.data));
    } catch (error: any) {
      setMembersMessage(`회원 목록을 불러오지 못했습니다: ${extractErrorMessage(error)}`);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [academyName]);

  useEffect(() => {
    loadMembers().catch(() => undefined);
  }, [loadMembers]);

  const handleSelectStudent = (student: StudentAttendanceRow) => {
    const eventdayDate = student.eventdayMs ? new Date(student.eventdayMs) : null;

    setSelectedStudent(student);
    setEditMemo(student.memo === '-' ? '' : student.memo);
    setAttendanceDate(eventdayDate ? formatDateToYmd(eventdayDate) : selectedDayYmd);
    setAttendanceTime(eventdayDate ? formatDateToTime(eventdayDate) : '00:00');
    setDetailMessage('');
  };

  const handleSaveMemo = async () => {
    if (!selectedStudent) {
      return;
    }

    if (!selectedStudent.tauserId) {
      setDetailMessage('수정할 회원의 tauser_id를 찾을 수 없습니다.');
      return;
    }

    setIsSavingMemo(true);
    setDetailMessage('');

    try {
      await membersAPI.updateTauser(selectedStudent.tauserId, {
        memo: editMemo,
      });

      const nextMemo = normalizeText(editMemo) || '-';
      setSelectedStudent((current) => (current ? { ...current, memo: nextMemo } : current));
      setDetailMessage('메모가 저장되었습니다.');
      await loadMembers();
    } catch (error: any) {
      setDetailMessage(`메모 저장 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsSavingMemo(false);
    }
  };

  const handleSaveEventday = async () => {
    if (!selectedStudent) {
      return;
    }

    if (!selectedStudent.tauserId) {
      setDetailMessage('보강 저장 대상의 tauser_id를 찾을 수 없습니다.');
      return;
    }

    if (!attendanceDate) {
      setDetailMessage('날짜를 선택해 주세요.');
      return;
    }

    if (!attendanceTime || !/^\d{2}:\d{2}$/.test(attendanceTime)) {
      setDetailMessage('시간(HH:MM)을 입력해 주세요.');
      return;
    }

    const nextEventday = new Date(`${attendanceDate}T${attendanceTime}:00`);
    const nextEventdayMs = nextEventday.getTime();
    if (Number.isNaN(nextEventdayMs)) {
      setDetailMessage('유효한 날짜/시간이 아닙니다.');
      return;
    }

    setIsSavingEventday(true);
    setDetailMessage('');

    try {
      await membersAPI.updateTauser(selectedStudent.tauserId, {
        eventday: nextEventdayMs,
      });

      setSelectedStudent((current) =>
        current
          ? {
              ...current,
              eventdayMs: nextEventdayMs,
            }
          : current
      );

      setMembers((currentMembers) =>
        currentMembers.map((member) => {
          if (getTauserId(member) !== selectedStudent.tauserId) {
            return member;
          }

          return {
            ...member,
            eventday: nextEventdayMs,
          };
        })
      );

      setDetailMessage('이벤트데이가 저장되었습니다.');
      await loadMembers();
    } catch (error: any) {
      setDetailMessage(`이벤트데이 저장 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsSavingEventday(false);
    }
  };

  const handleQuickSetStatus = useCallback(async (row: StudentAttendanceRow, nextCondition: '대기' | '출석' | '결석') => {
    if (!row.tauserId || !selectedDayYmd) {
      return;
    }

    const cooldownKey = `${row.tauserId}:${selectedDayYmd}`;
    if (quickStatusCooldownMap[cooldownKey]) {
      return;
    }

    setQuickStatusCooldownMap((current) => ({
      ...current,
      [cooldownKey]: true,
    }));

    window.setTimeout(() => {
      setQuickStatusCooldownMap((current) => {
        const next = { ...current };
        delete next[cooldownKey];
        return next;
      });
    }, 1000);

    const nextItem: CheckedDayItem = {
      day: selectedDayYmd,
      condition: nextCondition,
    };

    const merged = [...row.checkedDays.filter((item) => item.day !== nextItem.day), nextItem];
    const trimmed = merged.slice(-MAX_CHECKED_DAYS);

    try {
      await membersAPI.updateTauser(row.tauserId, {
        checked_days: trimmed,
      });

      setMembers((currentMembers) =>
        currentMembers.map((member) => {
          if (getTauserId(member) !== row.tauserId) {
            return member;
          }

          return {
            ...member,
            checked_days: trimmed,
          };
        })
      );

      setSelectedStudent((current) =>
        current && current.tauserId === row.tauserId
          ? {
              ...current,
              checkedDays: trimmed,
            }
          : current
      );
    } catch (error: any) {
      setMembersMessage(`퀵 상태 저장 실패: ${extractErrorMessage(error)}`);
    }
  }, [quickStatusCooldownMap, selectedDayYmd]);

  const handleCloseStudentDetail = () => {
    setSelectedStudent(null);
    setDetailMessage('');
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <main className="auth-page">
      <section className="auth-card admin-card">
        <p className="auth-eyebrow">ATTENDANCE CHECK</p>
        <h1>{displayName} 관리자님, 반갑습니다</h1>
        <p className="auth-description">요일을 선택하면 해당 요일 시간표가 있는 학생만 시간 순서대로 표시됩니다.</p>

        <div className="admin-action-row">
          <button
            type="button"
            className="auth-primary-button admin-refresh-button"
            onClick={() => {
              loadMembers().catch(() => undefined);
            }}
            disabled={isLoadingMembers}
          >
            {isLoadingMembers ? '갱신 중...' : '명단 갱신'}
          </button>
          <button type="button" className="auth-secondary-button admin-nav-button" onClick={() => navigate('/admin/home')}>
            관리자 홈
          </button>
          <button type="button" className="auth-secondary-button admin-logout-button" onClick={handleLogout}>
            로그아웃
          </button>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-item">
            <span>학원명</span>
            <strong>{academyName || '-'}</strong>
          </div>
          <div className="admin-summary-item">
            <span>선택 요일</span>
            <strong>
              {dayOptions.find((option) => option.key === selectedDay)?.label || '-'}
              {selectedWeekDates[selectedDay].monthDay ? `(${selectedWeekDates[selectedDay].monthDay})` : ''}
            </strong>
          </div>
          <div className="admin-summary-item">
            <span>표시 학생 수</span>
            <strong>{filteredRegularRows.length}</strong>
          </div>
        </div>

        {membersMessage ? <p className="auth-inline-message">{membersMessage}</p> : null}

        <section className="admin-table-section admin-table-stack">
          <div className="attendance-weekday-row" role="tablist" aria-label="요일 선택">
            {dayOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={selectedDay === option.key ? 'attendance-weekday-button active' : 'attendance-weekday-button'}
                onClick={() => setSelectedDay(option.key)}
              >
                {`${option.label}(${selectedWeekDates[option.key].monthDay})`}
              </button>
            ))}
          </div>

          <div className="admin-table-shell">
            <h3>보강 학생</h3>
            {!isLoadingMembers && remedialRows.length > 0 ? (
              <table className="admin-member-table">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>상태</th>
                    <th>학생</th>
                    <th>학교</th>
                    <th>반편성</th>
                    <th>메모</th>
                  </tr>
                </thead>
                <tbody>
                  {remedialRows.map((row) => (
                    <tr
                      key={`${row.key}-remedial`}
                      className={selectedStudent?.key === row.key ? 'admin-clickable-row selected-row' : 'admin-clickable-row'}
                      onClick={() => handleSelectStudent(row)}
                    >
                      {(() => {
                        const matched = row.checkedDays.find((item) => item.day === selectedDayYmd);
                        const statusText = conditionToDisplayStatus(matched?.condition || '');
                        const quickCooldownKey = `${row.tauserId}:${selectedDayYmd}`;
                        const isQuickButtonDisabled = Boolean(quickStatusCooldownMap[quickCooldownKey]);

                        return (
                          <>
                            <td>{row.eventdayTime}</td>
                            <td>
                              <div className="attendance-status-quick-group">
                                {quickStatusOptions.map((option) => {
                                  const isSelected = statusText === option;
                                  const stateClass =
                                    option === '대기'
                                      ? 'waiting'
                                      : option === '출석'
                                        ? 'present'
                                        : 'absent';

                                  return (
                                    <button
                                      key={`${row.key}-${option}-remedial`}
                                      type="button"
                                      className={`attendance-status-quick-button ${stateClass} ${isSelected ? 'is-selected' : ''}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleQuickSetStatus(row, option);
                                      }}
                                      disabled={isQuickButtonDisabled}
                                    >
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                            <td>{row.name}</td>
                            <td>{row.schoolName}</td>
                            <td>{row.memberClass}</td>
                            <td>{row.memo}</td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="auth-inline-message">
                {isLoadingMembers
                  ? '회원 목록을 불러오는 중입니다...'
                  : '선택한 요일에 보강 일정이 있는 학생이 없습니다.'}
              </p>
            )}
          </div>

          <div className="admin-table-shell">
            <h3>정규 시간표 학생</h3>
            <div className="admin-search-row">
              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="상태/학생/학교/반편성/메모 검색어 1"
                  value={regularSearchQuery1}
                  onChange={(event) => setRegularSearchQuery1(event.target.value)}
                />
                <button
                  type="button"
                  className="admin-search-clear"
                  onClick={() => setRegularSearchQuery1('')}
                  aria-label="검색어 1 초기화"
                  title="검색어 1 초기화"
                  disabled={!regularSearchQuery1.trim()}
                >
                  🗑
                </button>
              </div>

              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="상태/학생/학교/반편성/메모 검색어 2"
                  value={regularSearchQuery2}
                  onChange={(event) => setRegularSearchQuery2(event.target.value)}
                />
                <button
                  type="button"
                  className="admin-search-clear"
                  onClick={() => setRegularSearchQuery2('')}
                  aria-label="검색어 2 초기화"
                  title="검색어 2 초기화"
                  disabled={!regularSearchQuery2.trim()}
                >
                  🗑
                </button>
              </div>
            </div>

            {!isLoadingMembers && filteredRegularRows.length > 0 ? (
              <table className="admin-member-table">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>상태</th>
                    <th>학생</th>
                    <th>학교</th>
                    <th>반편성</th>
                    <th>메모</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegularRows.map((row) => (
                    <tr
                      key={row.key}
                      className={selectedStudent?.key === row.key ? 'admin-clickable-row selected-row' : 'admin-clickable-row'}
                      onClick={() => handleSelectStudent(row)}
                    >
                      {(() => {
                        const statusText = getDisplayStatusByDate(row.checkedDays, selectedDayYmd);
                        const quickCooldownKey = `${row.tauserId}:${selectedDayYmd}`;
                        const isQuickButtonDisabled = Boolean(quickStatusCooldownMap[quickCooldownKey]);

                        return (
                          <>
                            <td>{row[selectedDay]}</td>
                            <td>
                              <div className="attendance-status-quick-group">
                                {quickStatusOptions.map((option) => {
                                  const isSelected = statusText === option;
                                  const stateClass =
                                    option === '대기'
                                      ? 'waiting'
                                      : option === '출석'
                                        ? 'present'
                                        : 'absent';

                                  return (
                                    <button
                                      key={`${row.key}-${option}-regular`}
                                      type="button"
                                      className={`attendance-status-quick-button ${stateClass} ${isSelected ? 'is-selected' : ''}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleQuickSetStatus(row, option);
                                      }}
                                      disabled={isQuickButtonDisabled}
                                    >
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                            <td>{row.name}</td>
                            <td>{row.schoolName}</td>
                            <td>{row.memberClass}</td>
                            <td>{row.memo}</td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="auth-inline-message">
                {isLoadingMembers
                  ? '회원 목록을 불러오는 중입니다...'
                  : regularSearchQuery1.trim() || regularSearchQuery2.trim()
                    ? '검색 조건에 맞는 정규 시간표 학생이 없습니다.'
                    : '선택한 요일 시간표가 있는 학생이 없습니다.'}
              </p>
            )}
          </div>
        </section>

        {selectedStudent ? (
          <section className="admin-detail-card attendance-check-detail-card">
            <div className="admin-detail-header">
              <h2>출석 체크 상세</h2>
              <span>{selectedStudent.name}</span>
            </div>

            <div className="admin-contact-actions">
              <a
                className={canCallStudent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallStudent ? `tel:${selectedPhone}` : undefined}
                aria-disabled={!canCallStudent}
                onClick={(event) => {
                  if (!canCallStudent) {
                    event.preventDefault();
                  }
                }}
              >
                학생 전화걸기
              </a>
              <a
                className={canCallParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallParent ? `tel:${selectedParentPhone}` : undefined}
                aria-disabled={!canCallParent}
                onClick={(event) => {
                  if (!canCallParent) {
                    event.preventDefault();
                  }
                }}
              >
                학부모 전화걸기
              </a>
              <a
                className={canCallStudent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallStudent ? `sms:${selectedPhone}` : undefined}
                aria-disabled={!canCallStudent}
                onClick={(event) => {
                  if (!canCallStudent) {
                    event.preventDefault();
                  }
                }}
              >
                학생 문자하기
              </a>
              <a
                className={canCallParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallParent ? `sms:${selectedParentPhone}` : undefined}
                aria-disabled={!canCallParent}
                onClick={(event) => {
                  if (!canCallParent) {
                    event.preventDefault();
                  }
                }}
              >
                학부모 문자하기
              </a>
              <a
                className={canCallParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={attendanceNoticeSmsHref}
                aria-disabled={!canCallParent}
                onClick={(event) => {
                  if (!canCallParent) {
                    event.preventDefault();
                  }
                }}
              >
                출석알림 문자
              </a>
            </div>

            <div className="attendance-check-info-grid">
              <p><strong>학교</strong><span>{selectedStudent.schoolName}</span></p>
              <p><strong>반편성</strong><span>{selectedStudent.memberClass}</span></p>
              <label className="auth-field attendance-edit-field attendance-edit-field-wide">
                <span>메모</span>
                <input
                  type="text"
                  value={editMemo}
                  onChange={(event) => setEditMemo(event.target.value)}
                  placeholder="메모를 입력하세요"
                />
              </label>
            </div>

            <div className="attendance-info-action-row">
              <button
                type="button"
                className="auth-primary-button attendance-save-button"
                onClick={handleSaveMemo}
                disabled={isSavingMemo}
              >
                {isSavingMemo ? '저장 중...' : '메모 저장'}
              </button>
            </div>

            <div className="attendance-check-marking-grid">
              <label className="auth-field">
                <span>날짜</span>
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(event) => setAttendanceDate(event.target.value)}
                />
              </label>

              <label className="auth-field">
                <span>시간</span>
                <input
                  type="time"
                  value={attendanceTime}
                  onChange={(event) => setAttendanceTime(event.target.value)}
                />
              </label>

              <div className="attendance-info-action-row">
                <button
                  type="button"
                  className="auth-primary-button attendance-save-button"
                  onClick={handleSaveEventday}
                  disabled={isSavingEventday}
                >
                  {isSavingEventday ? '보강 저장 중...' : '보강 저장'}
                </button>
              </div>

              <div className="attendance-info-action-row">
                <button
                  type="button"
                  className="auth-secondary-button attendance-save-button"
                  onClick={handleCloseStudentDetail}
                >
                  학생정보 닫기
                </button>
              </div>
            </div>

            {detailMessage ? <p className="auth-inline-message">{detailMessage}</p> : null}
          </section>
        ) : null}
      </section>
    </main>
  );
};

export default AdminAttendanceCheckPage;
